import axiosInstance from 'services/api/config/api';

export interface VerdictIngestNumber {
    phoneNumber: string;
    rawDisplayResult: string;
    verdict: string;
    checksResolved: number;
    status?: string;
    releaseProposed: boolean;
    error?: string;
}

export interface VerdictIngestResult {
    rowsParsed: number;
    checksResolved: number;
    releaseProposals: string[];
    unmatched: string[];
    numbers: VerdictIngestNumber[];
    message: string;
}

// The backend parses the First Orion file itself so the column matching and the
// spam classification stay in one place: the same rules have to apply to a shell
// upload as to this one.
export const uploadVerdicts = (tenantId: string, file: File): Promise<VerdictIngestResult> => {
    const form = new FormData();
    form.append('file', file);

    // The Content-Type override is load-bearing: the shared instance defaults to
    // application/json, and axios serialises FormData to JSON when it sees that.
    return axiosInstance
        .post<VerdictIngestResult>(`/number-registration/${tenantId}/verdicts`, form, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((res: { data: VerdictIngestResult }) => res.data);
};

export interface ReleaseProposal {
    phoneNumberId: string;
    phoneNumber: string;
    proposedAt: string;
    lastVerdictAt?: string;
}

export interface ListReleaseProposalsResponse {
    proposals: ReleaseProposal[];
    count: number;
}

export const listReleaseProposals = (tenantId: string): Promise<ListReleaseProposalsResponse> => {
    return axiosInstance
        .get<ListReleaseProposalsResponse>(`/number-registration/${tenantId}/release-proposals`)
        .then((res: { data: ListReleaseProposalsResponse }) => res.data);
};

export interface SpamReplacementResult {
    oldNumber: string;
    newNumber: string;
    tenantId: string;
    tenantName: string;
    areaCode: number;
    releaseError?: string;
    purchaseError?: string;
    success: boolean;
}

export interface ReplaceSpamNumbersResult {
    totalSpamNumbers: number;
    replaced: number;
    failed: number;
    notFound: number;
    results: SpamReplacementResult[];
    message: string;
}

export interface ReleaseDecisionResult {
    approved: string[];
    rejected: string[];
    refused: string[];
    errors?: Record<string, string>;
    replacement?: ReplaceSpamNumbersResult;
    actedBy: string;
    actedAt: string;
    numbersAsked: number;
}

export const approveReleaseProposals = (tenantId: string, phoneNumbers: string[]): Promise<ReleaseDecisionResult> => {
    return axiosInstance
        .post<ReleaseDecisionResult>(`/number-registration/${tenantId}/release-proposals/approve`, { phoneNumbers })
        .then((res: { data: ReleaseDecisionResult }) => res.data);
};

export const rejectReleaseProposals = (tenantId: string, phoneNumbers: string[]): Promise<ReleaseDecisionResult> => {
    return axiosInstance
        .post<ReleaseDecisionResult>(`/number-registration/${tenantId}/release-proposals/reject`, { phoneNumbers })
        .then((res: { data: ReleaseDecisionResult }) => res.data);
};

const numberRegistrationService = {
    uploadVerdicts,
    listReleaseProposals,
    approveReleaseProposals,
    rejectReleaseProposals,
};

export default numberRegistrationService;
